import {View} from '@fantasmagames/slot/src/common/view/View';
import {Spine} from '@fantasmagames/core/src/spine/Spine';
import {SpriteButton} from '@fantasmagames/core/src/display/SpriteButton';
import {FreeGamesData, FreeGamesInProgress} from '@fantasmagames/slot/src/logic/integration/FreeGamesData';
import {SpineAnimation} from '@fantasmagames/core/src/display/SpineAnimation';

export class InFreeRoundsView extends View {
	public static readonly DISCARD_PRESSED: string = 'DISCARD_PRESSED';

	protected _ingameFreeGamesText: PIXI.Text;
	protected _ingameFreeGamesStyle: PIXI.TextStyle;

	protected _inGameDiscardButton: SpriteButton;
	protected _isButtonVisible: boolean = true;

	protected _mainAnimation: SpineAnimation;

	protected _isEnabled: boolean = true;
	protected _isNoDiscardButton: boolean = false;

	public showInGameScreen(freeGamesData: FreeGamesData, lastFreeGame: FreeGamesInProgress, isAutoplay: boolean): void {
		this.show();
		let animationName: string = 'IN_GAME_IDLE';
		if (lastFreeGame && lastFreeGame.gamesRemaining === 0) {
			animationName = 'IN_GAME_CONCLUSION';
		}

		this.showMenuAnimation(animationName, false);

		if (this._isButtonVisible) {
			this.showDiscardButton();
		} else {
			this.hideDiscardButton();
		}

		if (lastFreeGame && lastFreeGame.gamesRemaining === 0) {
			this._inGameDiscardButton.callback = null;
			this._inGameDiscardButton.interactive = false;
			this._inGameDiscardButton.buttonMode = false;
			this._isNoDiscardButton = true;
		} else {
			this._inGameDiscardButton.callback = this.onDiscardPressed.bind(this);
			this._isNoDiscardButton = false;
		}

		if (isAutoplay) {
			this.disableDiscardButton();
		}

		if (!this._isEnabled) {
			this.disableDiscardButton();
		}

		let freeGamesText: string = this.translator.getObject('BaseStrings', 'generic', 'g_freegames_freegamesn') || `FREE GAMES`;
		if (lastFreeGame) {
			freeGamesText = freeGamesText.replace('{0}', `${lastFreeGame.gamesRemaining}`);
			freeGamesText = freeGamesText.replace('{1}', `${lastFreeGame.gamesRemaining + lastFreeGame.gamesPlayed}`);
		} else {
			freeGamesText = freeGamesText.replace('{0}', `${freeGamesData.gamesRemaining}`);
			freeGamesText = freeGamesText.replace('{1}', `${freeGamesData.gamesAwarded}`);
		}
		this._ingameFreeGamesText.text = freeGamesText;
		this._ingameFreeGamesText.scale.x = 0.8;
		this._ingameFreeGamesText.scale.y = -0.8;

	}

	public disableDiscardButton(): void {
		this._isEnabled = false;
		if (this._inGameDiscardButton) {
			this._inGameDiscardButton.interactive = false;
			this._inGameDiscardButton.buttonMode = false;
		}
	}

	public enableDiscardButton(): void {
		if (this._isNoDiscardButton) {
			return;
		}

		this._isEnabled = true;
		if (this._inGameDiscardButton) {
			this._inGameDiscardButton.interactive = true;
			this._inGameDiscardButton.buttonMode = true;
		}
	}

	public hideDiscardButton(): void {
		if (this._inGameDiscardButton) {
			this._inGameDiscardButton.visible = false;
		}
		this._isButtonVisible = false;
	}

	public showDiscardButton(): void {
		if (this._inGameDiscardButton) {
			this._inGameDiscardButton.visible = true;
		}
		this._isButtonVisible = true;
	}

	public removeScreen(): void {
		if (this._mainAnimation) {
			this._mainAnimation.forceDestroy();
			this.updateManager.remove(this._mainAnimation);
			this.container.removeChild(this._mainAnimation.container);
		}
	}

	protected setupButtons(screenSpine: Spine): void {
		this._inGameDiscardButton = new SpriteButton();

		this._ingameFreeGamesStyle = this.textStyles.breadTextFont;
		this._ingameFreeGamesStyle.wordWrapWidth = this.resolutionManager.resolution.width;

		this._ingameFreeGamesText = new PIXI.Text('', this._ingameFreeGamesStyle);
		this._ingameFreeGamesText.anchor.set(0.5);

		////////////////////////////////////////////////////////////////////
		// In BaseGame Discard Button
		////////////////////////////////////////////////////////////////////
		const ingameDiscardButtonUp: PIXI.Sprite = new PIXI.Sprite(this.resources.getSpritesheetTexture('MICROGAMING.json', 'FREEGAMES_DISCARD_BUTTON.png'));
		const ingameDiscardButtonDown: PIXI.Sprite = new PIXI.Sprite(this.resources.getSpritesheetTexture('MICROGAMING.json', 'FREEGAMES_DISCARD_BUTTON_CLICKED.png'));
		const ingameDiscardButtonOver: PIXI.Sprite = new PIXI.Sprite(this.resources.getSpritesheetTexture('MICROGAMING.json', 'FREEGAMES_DISCARD_BUTTON.png'));
		const ingameDiscardButtonDisabled: PIXI.Sprite = new PIXI.Sprite(this.resources.getSpritesheetTexture('MICROGAMING.json', 'FREEGAMES_DISCARD_BUTTON.png'));

		ingameDiscardButtonUp.anchor.set(0.5, 0.5);
		ingameDiscardButtonDown.anchor.set(0.5, 0.5);
		ingameDiscardButtonOver.anchor.set(0.5, 0.5);
		ingameDiscardButtonDisabled.anchor.set(0.5, 0.5);

		this._inGameDiscardButton.setFrames(ingameDiscardButtonUp, ingameDiscardButtonDown, ingameDiscardButtonOver, ingameDiscardButtonDisabled);

		const ingameFreeGamesTextSlot = screenSpine.slotContainers[screenSpine.skeleton.findSlotIndex('FREE_GAMES_TEXT')];

		ingameFreeGamesTextSlot.addChild(this._ingameFreeGamesText);
		this._ingameFreeGamesText.transform = ingameFreeGamesTextSlot.children[0].transform;
		ingameFreeGamesTextSlot.children[0].visible = false;

		const inGameDiscardButtonSlot = screenSpine.slotContainers[screenSpine.skeleton.findSlotIndex('IN_GAME_DISCARD_BUTTON')];

		inGameDiscardButtonSlot.addChild(this._inGameDiscardButton);
		this._inGameDiscardButton.transform = inGameDiscardButtonSlot.children[0].transform;
		inGameDiscardButtonSlot.children[0].visible = false;
	}

	protected onDiscardPressed(): void {
		this.emit(InFreeRoundsView.DISCARD_PRESSED);
	}

	protected showMenuAnimation(animationName: string, loop: boolean, stopOnComplete: boolean = true): SpineAnimation {
		this.removeScreen();

		const spine: Spine = new Spine(this.resources.getSpine('FREE-GAMES_SCREENS.json'));
		this.setupButtons(spine);
		this._mainAnimation = new SpineAnimation(spine, animationName, loop, true);
		this._mainAnimation.container.position.set(this.resolutionManager.halfResolution.width,
			this.resolutionManager.halfResolution.height);
		this.container.addChild(this._mainAnimation.container);
		if (stopOnComplete) {
			this._mainAnimation.setOnComplete(() => {
				this._mainAnimation.stop();
			});
		}

		this.updateManager.add(this._mainAnimation);
		this._mainAnimation.start();

		return this._mainAnimation;
	}

}


// WEBPACK FOOTER //
// ./@fantasmagames/slot/src/modules/microgaming/inFreeRounds/InFreeRoundsView.ts